import { create } from 'zustand';
import type {
  CameraListQuery,
  CameraStatus,
  CameraType,
  RecordingMode,
} from '../../api/flashbackApi/types.js';

export type GridLayout = '1x1' | '2x2' | '3x3' | '4x4';
export type SortBy = NonNullable<CameraListQuery['sortBy']>;
export type SortOrder = NonNullable<CameraListQuery['sortOrder']>;

export interface DashboardFilters {
  search: string;
  status?: CameraStatus;
  type?: CameraType;
  groupId?: number;
  recordingMode?: RecordingMode;
  sortBy: SortBy;
  sortOrder: SortOrder;
}

interface DashboardState {
  filters: DashboardFilters;
  layout: GridLayout;
  setSearch: (search: string) => void;
  setFilter: <K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]) => void;
  setLayout: (layout: GridLayout) => void;
  resetFilters: () => void;
}

const DEFAULT_FILTERS: DashboardFilters = {
  search: '',
  sortBy: 'name',
  sortOrder: 'asc',
};

export const useDashboardStore = create<DashboardState>((set) => ({
  filters: DEFAULT_FILTERS,
  layout: '3x3',
  setSearch: (search) =>
    set((s) => ({ filters: { ...s.filters, search } })),
  setFilter: (key, value) =>
    set((s) => ({ filters: { ...s.filters, [key]: value } })),
  setLayout: (layout) => set({ layout }),
  resetFilters: () => set({ filters: DEFAULT_FILTERS }),
}));

export function buildCameraQuery(filters: DashboardFilters): CameraListQuery {
  return {
    search: filters.search.trim() || undefined,
    status: filters.status,
    type: filters.type,
    groupId: filters.groupId,
    recordingMode: filters.recordingMode,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
  };
}
